import { useCallback, useEffect, useRef, useState } from "react"
import type { Map as LeafletMap } from "leaflet"

import type { LimitingSpanHighlights } from "helpers/hooks/powerlines/useLimitingSpanFrequency"
import type { SpanDatum } from "types/spans.types"

import {
  FLY_TO_DURATION_S,
  FLY_TO_SETTLE_MS,
  FLY_TO_ZOOM,
  type SpanGeoById,
  type SpanMapState,
} from "./helpers"

interface UseSpanSelectionInput {
  // Resets the touch seeding: a new line gets its own first span.
  powerlineId: number
  isNarrow: boolean
  state: SpanMapState
  spanData: SpanDatum[]
  highlights: LimitingSpanHighlights
  // Where each span is, so a selection can fly the map there.
  spanGeoById: SpanGeoById
}

interface UseSpanSelectionResult {
  hoveredSpanId: number | null
  selectedSpanId: number | null
  // Leaflet's own popup open/close drives the selection back, so the card
  // hands this to the map layer directly.
  setSelectedSpanId: (spanId: number | null) => void
  // A fly-to is running. The card's height effect waits on it: a resize
  // mid-pan reaches leaflet as an `invalidateSize` and derails it.
  isFlying: boolean
  handleHoverSpan: (spanId: number | null) => void
  handleSelectSpan: (spanId: number | null) => void
  handleMapReady: (map: LeafletMap) => void
}

/**
 * Which span the card is pointing at, on the map and in the ribbon: the hover,
 * the selection, and the fly-to the selection starts.
 *
 * The three are one hook because they read each other — the hover guard needs
 * to know a pan is running, and selecting a span sets the hover as well.
 */
const useSpanSelection = ({
  powerlineId,
  isNarrow,
  state,
  spanData,
  highlights,
  spanGeoById,
}: UseSpanSelectionInput): UseSpanSelectionResult => {
  const [hoveredSpanId, setHoveredSpanId] = useState<number | null>(null)
  const [selectedSpanId, setSelectedSpanId] = useState<number | null>(null)
  const mapRef = useRef<LeafletMap | null>(null)
  // True from the moment a fly-to starts until the map has settled. The ref is
  // for the hover guard, which needs it now; the state re-runs the card's
  // height effect once the pan lands.
  const isFlyingRef = useRef(false)
  const [isFlying, setIsFlying] = useState(false)
  const flySettleTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(
    () => () => {
      if (flySettleTimerRef.current) clearTimeout(flySettleTimerRef.current)
    },
    [],
  )

  // Hover re-orders the map's SVG paths. Doing that while leaflet is
  // transforming the panes makes the view jump, so ignore hover mid-flight.
  const handleHoverSpan = useCallback((spanId: number | null) => {
    if (isFlyingRef.current) return
    setHoveredSpanId(spanId)
  }, [])

  // Selecting a span flies to it and opens its popup
  const handleSelectSpan = useCallback(
    (spanId: number | null) => {
      if (spanId == null) return
      setSelectedSpanId(spanId)
      const target = spanGeoById.get(spanId)?.midpoint
      if (target && mapRef.current) {
        // Without this, a hover left over from the click dims the span we're
        // flying to for the whole trip.
        setHoveredSpanId(spanId)
        isFlyingRef.current = true
        setIsFlying(true)
        if (flySettleTimerRef.current) clearTimeout(flySettleTimerRef.current)
        flySettleTimerRef.current = setTimeout(
          () => {
            isFlyingRef.current = false
            setIsFlying(false)
          },
          FLY_TO_DURATION_S * 1000 + FLY_TO_SETTLE_MS,
        )
        mapRef.current.flyTo(target, FLY_TO_ZOOM, { duration: FLY_TO_DURATION_S })
      }
    },
    [spanGeoById],
  )

  const handleMapReady = useCallback((map: LeafletMap) => {
    mapRef.current = map
  }, [])

  // Touch has no hover, so the selection is the only thing that can fill the
  // readout, and an empty one gives no hint that tapping is what fills it.
  // Seed it with the span that limits most often.
  const hasSeededRef = useRef(false)
  useEffect(() => {
    hasSeededRef.current = false
  }, [powerlineId])

  useEffect(() => {
    if (!isNarrow || hasSeededRef.current) return
    if (selectedSpanId != null) {
      hasSeededRef.current = true
      return
    }
    const peak = highlights.peakAarSpanId ?? highlights.peakDlrSpanId
    if (peak != null) {
      hasSeededRef.current = true
      setSelectedSpanId(peak)
      return
    }
    // Neutral prints no figures, so any span reads as an example of what
    // tapping gives you, not as a measurement of zero.
    if (state === "neutral" && spanData[0]?.spanId != null) {
      hasSeededRef.current = true
      setSelectedSpanId(spanData[0].spanId)
      return
    }
    // No peak yet is the normal first render, so wait.
  }, [
    isNarrow,
    state,
    spanData,
    highlights.peakAarSpanId,
    highlights.peakDlrSpanId,
    selectedSpanId,
  ])

  return {
    hoveredSpanId,
    selectedSpanId,
    setSelectedSpanId,
    isFlying,
    handleHoverSpan,
    handleSelectSpan,
    handleMapReady,
  }
}

export default useSpanSelection
